/**
 * The /leads page's state, held in the URL.
 *
 * Leads and Lead Stats used to be two routes, each with its own filter state
 * that reset on every visit. They are one page now, and everything that
 * scopes it (tab, source, CTA, classification, junk, the list's date window
 * and archive view, the stats range) lives in the query string, so a
 * bookmark, a pasted link or an email deep link lands on exactly the slice it
 * was taken from. Defaults are left out, so a plain /leads stays plain.
 *
 * Pure functions only: the page reads with `parseLeadsParams` and writes with
 * `leadsParamsFor`, and the tests exercise both without a router.
 */
import { DEFAULT_LEAD_STATS_RANGE, LEAD_STATS_RANGE_OPTIONS } from "@/lib/lead-stats-view"
import type { LeadStatsRangeChoice } from "@/lib/lead-stats-view"

export type LeadsTab = "inbox" | "stats"

export const LEAD_SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: "cta", label: "CTA popups" },
  { value: "webhook", label: "Form webhook" },
  { value: "wp_gate", label: "Gated content" },
  { value: "callrail", label: "Phone calls" },
  { value: "import", label: "Imported history" },
] as const

const SOURCE_VALUES = new Set<string>(LEAD_SOURCE_OPTIONS.map((option) => option.value))

const CLASSIFICATION_STATES = new Set([
  "classified", "unclassified", "needs_review", "pending", "processing", "deferred", "failed", "skipped",
])

export interface LeadsPageState {
  tab: LeadsTab
  /** "all" or one of LEAD_SOURCE_OPTIONS. Shared by both tabs. */
  source: string
  /** "all" or a CTA id as a string. Shared by both tabs. */
  cta: string
  /** Test and internal leads are hidden unless this is on. Shared. */
  includeJunk: boolean
  classification: { category: string; subtype: string; state: string }
  /** The inbox's captured-date window, bare yyyy-mm-dd; "" = unbounded. */
  dateFrom: string
  dateTo: string
  /** The inbox shows archived leads only. */
  archived: boolean
  /** The stats tab's window. */
  range: LeadStatsRangeChoice
}

export const DEFAULT_LEADS_STATE: LeadsPageState = {
  tab: "inbox",
  source: "all",
  cta: "all",
  includeJunk: false,
  classification: { category: "all", subtype: "all", state: "all" },
  dateFrom: "",
  dateTo: "",
  archived: false,
  range: DEFAULT_LEAD_STATS_RANGE,
}

/** "Today" → "today", "All time" → "all", "30d" → "30d". */
export function rangeSlug(choice: LeadStatsRangeChoice): string {
  if ("all" in choice) return "all"
  return choice.label.toLowerCase()
}

function rangeFromSlug(slug: string | null): LeadStatsRangeChoice {
  if (!slug) return DEFAULT_LEAD_STATS_RANGE
  return LEAD_STATS_RANGE_OPTIONS.find((option) => rangeSlug(option) === slug.toLowerCase()) ?? DEFAULT_LEAD_STATS_RANGE
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

function day(value: string | null): string {
  return value && ISO_DAY.test(value) ? value : ""
}

function truthy(value: string | null): boolean {
  return value === "1" || value === "true"
}

/** Free-text classification values: kept as given, capped like the API caps them. */
function label(value: string | null): string {
  const trimmed = value?.trim() ?? ""
  return trimmed === "" ? "all" : trimmed.slice(0, 100)
}

/**
 * Read the page state from a query string. Anything unrecognised falls back
 * to its default rather than being sent to the API, which would 422 the page
 * on a hand-edited or stale link.
 */
export function parseLeadsParams(params: URLSearchParams): LeadsPageState {
  const source = params.get("source") ?? "all"
  const cta = params.get("cta") ?? "all"
  const state = params.get("state") ?? "all"
  const dateFrom = day(params.get("from"))
  let dateTo = day(params.get("to"))
  // The API rejects an inverted window; drop the end rather than the start.
  if (dateFrom && dateTo && dateTo < dateFrom) dateTo = ""

  return {
    tab: params.get("tab") === "stats" ? "stats" : "inbox",
    source: SOURCE_VALUES.has(source) ? source : "all",
    cta: /^\d+$/.test(cta) ? cta : "all",
    includeJunk: truthy(params.get("junk")),
    classification: {
      category: label(params.get("category")),
      subtype: label(params.get("subtype")),
      state: CLASSIFICATION_STATES.has(state) ? state : "all",
    },
    dateFrom,
    dateTo,
    archived: truthy(params.get("archived")),
    range: rangeFromSlug(params.get("range")),
  }
}

/** The query string for a state, defaults omitted, in a stable key order. */
export function leadsParamsFor(state: LeadsPageState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.tab !== "inbox") params.set("tab", state.tab)
  if (state.source !== "all") params.set("source", state.source)
  if (state.cta !== "all") params.set("cta", state.cta)
  if (state.classification.category !== "all") params.set("category", state.classification.category)
  if (state.classification.subtype !== "all") params.set("subtype", state.classification.subtype)
  if (state.classification.state !== "all") params.set("state", state.classification.state)
  if (state.includeJunk) params.set("junk", "1")
  if (state.dateFrom) params.set("from", state.dateFrom)
  if (state.dateTo) params.set("to", state.dateTo)
  if (state.archived) params.set("archived", "1")
  if (rangeSlug(state.range) !== rangeSlug(DEFAULT_LEAD_STATS_RANGE)) params.set("range", rangeSlug(state.range))
  return params
}

/**
 * Where an old /leads/stats link lands: the Stats tab of /leads, keeping
 * whatever else the link carried (a ?license= from an email included, which
 * the auth layer reads and strips on its own).
 */
export function legacyStatsTarget(search: string): string {
  const params = new URLSearchParams(search)
  params.set("tab", "stats")
  return `/leads?${params.toString()}`
}
